import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaShoppingBag, FaReadme } from "react-icons/fa";

import {
  CenterText,
  Container,
  CoursesGrid,
  CoursesSection,
  Hero,
  HeroVideo,
  SectionHeader,
  StoryContent,
  StoryImage,
  StorySection,
  StoryText,
  CTAButton,
  HeroOverlay,
  HeroCenter,
  HeroTagline,
  ScrollChevrons,
  Logo,
  Relative,
} from "./HomePage.styles";

import HeroVideoUrl from "../../assets/videos/heroVideo.webm";
import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import CourseCard from "../../components/CourseCard";
import {
  MiniSection,
  MiniDivider,
  MiniGrid,
} from "../CoursesPage/CoursesPage.styles";
import {
  selectFeaturedCourses,
  selectFeaturedLoading,
  selectCoursesError,
  selectCourses,
} from "../../store/slices/courses/slice";
import { getFeaturedCourses } from "../../store/slices/courses/asyncThunks";

import LogoImg from "../../assets/images/logo-04.png";
import MeImg from "../../assets/images/mm.webp";

// Hook for intersection observer
const useIntersectionObserver = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      // { threshold, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, threshold]);

  return [setElement, isVisible];
};

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const featuredCourses = useSelector(selectFeaturedCourses);
  const featuredLoading = useSelector(selectFeaturedLoading);
  const coursesError = useSelector(selectCoursesError);
  // Pun katalog (učitava ga Footer) — iz njega izdvajamo mini edukacije
  const courses = useSelector(selectCourses);
  const miniCourses = courses.filter((course) => course.isMini);

  // Fetch featured courses on component mount
  useEffect(() => {
    dispatch(getFeaturedCourses(3));
  }, [dispatch]);

  // Intersection observer hooks

  const [coursesRef, coursesVisible] = useIntersectionObserver();

  const navigateWithScroll = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer>
      <SEO path="/" />

      {/* Hero Section */}
      <Hero id="home">
        <Relative>
          <HeroVideo autoPlay muted loop playsInline>
            <source src={HeroVideoUrl} type="video/webm" />
          </HeroVideo>

          <HeroOverlay />

          <HeroCenter>
            <Logo src={LogoImg} alt="MITOVKA BeautySpot logo" />
            <HeroTagline>Lash &amp; Brow online kursevi</HeroTagline>
          </HeroCenter>

          <ScrollChevrons
            type="button"
            aria-label="Skroluj do kurseva"
            onClick={() =>
              document
                .getElementById("courses")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <span />
            <span />
          </ScrollChevrons>
        </Relative>
      </Hero>

      {/* Mini edukacije — prva sekcija ispod hero-a, kao na /kursevi */}
      {miniCourses.length > 0 && (
        <MiniSection>
          <MiniDivider>
            <span>
              Mini edukacije <small>Novo</small>
            </span>
          </MiniDivider>
          <MiniGrid>
            {miniCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                variant="wide"
                animate={true}
                offMaxWidth={true}
              />
            ))}
          </MiniGrid>
        </MiniSection>
      )}

      {/* Courses Section */}
      <CoursesSection id="courses">
        <Container>
          <SectionHeader>
            <h2>Dostupni Kursevi</h2>
            <p>Pretvori svoju veštinu u profitabilni biznis.</p>
          </SectionHeader>

          <CoursesGrid ref={coursesRef}>
            {featuredLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <l-line-spinner
                  size="32"
                  stroke="3"
                  speed="1"
                  color="var(--accent)"
                />

                <div>Učitavanje kurseva...</div>
              </div>
            ) : coursesError ? (
              <div>Greška pri učitavanju kurseva: {coursesError}</div>
            ) : featuredCourses.length === 0 ? (
              <div>Nema dostupnih kurseva.</div>
            ) : (
              featuredCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  variant="homepage"
                  animate={coursesVisible}
                />
              ))
            )}
          </CoursesGrid>
          {/* CTA to view all courses */}
          <CenterText>
            <CTAButton onClick={() => navigateWithScroll("/kursevi")}>
              <FaShoppingBag />
              Pogledaj Sve Kurseve
            </CTAButton>
          </CenterText>
        </Container>
      </CoursesSection>

      {/* Story Section */}
      <StorySection id="about">
        <Container>
          <StoryContent>
            <StoryText>
              <h3>O Meni</h3>
              <p>
                Ja sam <span className="highlight">Maša Mitov</span>, edukator,
                kreator i vlasnica <span className="highlight">MITOVKA</span>{" "}
                salona u Novom Sadu. U beauty industriju sam ušla{" "}
                <span className="highlight">2020. godine</span> i nisam ni
                slutila da će se moja ljubav prema trepavicama i obrvama
                pretvoriti iz hobija u ozbiljan biznis.
              </p>

              <p>
                Godinama sam{" "}
                <span className="highlight">skupljala informacije</span>,
                istraživala, grešila i učila. Kroz moje ruke je prošlo{" "}
                <span className="highlight">bezbroj klijenata</span>, a svoje
                znanje sam prenela na više od{" "}
                <span className="highlight">500+</span> zadovoljnih učenica koje
                su od mene učile kako uživo, tako i online.
              </p>

              <p>
                Iz tog razloga sam odlučila da svoje znanje prenesem tebi kroz
                moju novu online edukaciju za{" "}
                <span className="highlight">Lash</span> i{" "}
                <span className="highlight">Brow Lift</span>.
              </p>
              <div>
                <CTAButton onClick={() => navigateWithScroll("o-meni")}>
                  <FaReadme size={16} />
                  Pročitaj više
                </CTAButton>
              </div>
            </StoryText>

            <StoryImage src={MeImg} alt="Profesionalni Portret" />
          </StoryContent>
        </Container>
      </StorySection>
    </MainContainer>
  );
};

export default HomePage;
